import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import { MenuFirstIcon, MenuLastIcon } from '@mc/wink-icons';
import {
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  ArrowRight,
} from '@design-systems/icons';
import { mcdsFlagCheck } from '@mc/wink/helpers/utils-ts';
import IconButton from '../IconButton';
import Button from '../Button';
import InputNumber from '../InputNumber';
import Text from '../Text';
import ClusterLayout from '../ClusterLayout';
import { TranslatePagination } from './TranslatePagination';
import stylesheet from './Pagination.css';

export type PaginationVariant = 'simple' | 'basic' | 'expanded' | 'input';

export type PaginationProps = {
  currentPage: number;
  totalPages: number;
  onInputChange?: (page: number) => void;
  onInputBlur?: (page: number) => void;
  onFirstPage?: () => void;
  onPreviousPage?: () => void;
  onNextPage?: () => void;
  onLastPage?: () => void;
  variant?: PaginationVariant;
};

const Pagination = React.forwardRef<HTMLDivElement, PaginationProps>(
  function Pagination(
    {
      currentPage,
      totalPages,
      onInputChange,
      onInputBlur,
      onFirstPage,
      onPreviousPage,
      onNextPage,
      onLastPage,
      variant = 'basic',
    },
    forwardedRef,
  ) {
    const isFirstPage = currentPage <= 1;
    const isLastPage = currentPage >= totalPages;
    const showFirstLastButtons = variant === 'expanded' || variant === 'input';
    const showPageNumbers = variant !== 'simple';
    const showInput = variant === 'input';
    const [inputValue, setInputValue] = useState(currentPage.toString());

    const totalPagesCharacters = String(totalPages).length;
    const inputCharacterWidth = Math.max(totalPagesCharacters, 2);

    useEffect(() => {
      setInputValue(currentPage.toString());
    }, [currentPage]);

    const handleInputChange = (value: string) => {
      const pageNum = parseInt(value, 10);
      if (onInputChange && Number.isFinite(pageNum)) {
        onInputChange(pageNum);
      }
      setInputValue(value);
    };

    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const pageNum = parseInt(e.target.value, 10);
      if (onInputBlur && Number.isFinite(pageNum)) {
        onInputBlur(pageNum);
      }
    };

    const {
      goToFirstPageLabel,
      goToLastPageLabel,
      goToNextPageLabel,
      goToPreviousPageLabel,
      navigationLabel,
      nextText,
      ofText,
      pageNumberLabelText,
      previousText,
    } = TranslatePagination(totalPages);

    const isReleased = mcdsFlagCheck(
      'xp_mcds_redesign_components_organisms_part_2',
    );

    return (
      isReleased && (
        <nav ref={forwardedRef} aria-label={navigationLabel}>
          <ClusterLayout
            as="ul"
            gap={0}
            justifyContent={variant === 'simple' ? 'space-between' : undefined}
            className={cx({ [stylesheet.fullWidth]: variant === 'simple' })}
          >
            {showFirstLastButtons && (
              <li>
                <IconButton
                  type="quaternary"
                  size="small"
                  icon={<MenuFirstIcon />}
                  onClick={onFirstPage}
                  disabled={isFirstPage}
                  label={goToFirstPageLabel}
                />
              </li>
            )}
            <li>
              {variant === 'simple' ? (
                <Button
                  type="quaternary"
                  onClick={onPreviousPage}
                  disabled={isFirstPage}
                  label={goToPreviousPageLabel}
                >
                  <ArrowLeft size="xsmall" />
                  <Text as="span" appearance="small">
                    {previousText}
                  </Text>
                </Button>
              ) : (
                <IconButton
                  type="quaternary"
                  size="small"
                  icon={<ChevronLeft />}
                  onClick={onPreviousPage}
                  disabled={isFirstPage}
                  label={goToPreviousPageLabel}
                />
              )}
            </li>
            {showPageNumbers && (
              <li>
                {showInput ? (
                  <ClusterLayout
                    gap={1}
                    className={stylesheet.pageNumberInputWrapper}
                  >
                    <InputNumber
                      value={inputValue}
                      onChange={handleInputChange}
                      onBlur={handleInputBlur}
                      inputSize="medium"
                      hideLabel
                      className={stylesheet.pageNumberInput}
                      size={inputCharacterWidth}
                      label={pageNumberLabelText}
                    />
                    <div>
                      <Text as="span" appearance="small-tertiary">
                        {ofText}
                      </Text>
                      <Text as="span" appearance="small">
                        {totalPages}
                      </Text>
                    </div>
                  </ClusterLayout>
                ) : (
                  <div
                    className={stylesheet.pageTextContainer}
                    role="status"
                    aria-live="polite"
                  >
                    <Text as="span" appearance="small">
                      {currentPage}
                    </Text>
                    <Text as="span" appearance="small-tertiary">
                      {ofText}
                    </Text>
                    <Text as="span" appearance="small">
                      {totalPages}
                    </Text>
                  </div>
                )}
              </li>
            )}
            <li>
              {variant === 'simple' ? (
                <Button
                  type="quaternary"
                  onClick={onNextPage}
                  disabled={isLastPage}
                  aria-label={goToNextPageLabel}
                >
                  <Text as="span" appearance="small">
                    {nextText}
                  </Text>
                  <ArrowRight size="xsmall" />
                </Button>
              ) : (
                <IconButton
                  type="quaternary"
                  size="small"
                  icon={<ChevronRight />}
                  onClick={onNextPage}
                  disabled={isLastPage}
                  label={goToNextPageLabel}
                />
              )}
            </li>
            {showFirstLastButtons && (
              <li>
                <IconButton
                  type="quaternary"
                  size="small"
                  icon={<MenuLastIcon />}
                  label={goToLastPageLabel}
                  onClick={onLastPage}
                  disabled={isLastPage}
                />
              </li>
            )}
          </ClusterLayout>
        </nav>
      )
    );
  },
);

export default Pagination;
